import { useEffect, useState, useCallback, useRef } from 'react';
import {
  IonItem,
  IonButtons,
  IonContent,
  IonText,
  IonIcon,
  IonHeader,
  IonList,
  IonPage,
  IonSearchbar,
  IonToolbar,
  IonChip,
  IonLabel,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSpinner,
  useIonModal,
} from '@ionic/react';
import throttle from 'lodash.throttle';
import { closeCircle, addCircle } from 'ionicons/icons';
import { getClinics, getClinicClassifications } from 'global';
import './ModalClinics.scss';
import { useConfirmAddress } from 'hooks/useConfirmAddress';
import { IClinic, IClinicClassification } from 'models';

const PAGE_SIZE = 50;

const emptyClinic = (overrides: Partial<IClinic> = {}): IClinic => ({
  clinic_name: '',
  crm_id: '',
  zip: '',
  state_cd: '',
  address_1: '',
  city: '',
  created_at: '',
  updated_at: '',
  ...overrides,
});

export const ClinicModalBody: React.FC<{
  onDismiss: () => void;
  onSelectItem: (clinic: IClinic) => void;
  state?: string;
}> = ({ onSelectItem, onDismiss, state = '' }) => {
  const [query, setQuery] = useState('');
  const [classification, setClassification] = useState<string>('');
  const [classifications, setClassifications] = useState<IClinicClassification[]>(
    [],
  );
  const [clinics, setClinics] = useState<IClinic[]>([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  // Load the classification facets once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getClinicClassifications();
      if (!cancelled && res?.ok) {
        setClassifications(res.data || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPage = useCallback(
    async (
      nextQuery: string,
      nextClassification: string,
      nextState: string,
      nextOffset: number,
      append: boolean,
    ) => {
      // Don't fire the empty-state request — the user must type or pick a chip.
      // A state filter alone is intentionally allowed to browse a state's clinics.
      if (!nextQuery && !nextClassification && !nextState) {
        setClinics([]);
        setTotal(0);
        setOffset(0);
        return;
      }

      const seq = ++requestSeq.current;
      setLoading(true);
      const res = await getClinics({
        name: nextQuery,
        state: nextState,
        classification: nextClassification,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      // Discard stale responses (user typed again before this one returned).
      if (seq !== requestSeq.current) return;

      if (res?.ok) {
        const incoming: IClinic[] = res.data || [];
        setClinics((prev) => (append ? [...prev, ...incoming] : incoming));
        setTotal(res.total || 0);
        setOffset(nextOffset + incoming.length);
      }
      setLoading(false);
    },
    [],
  );

  // Throttled search-on-type. Resets pagination.
  const throttledSearch = useRef(
    throttle(
      (nextQuery: string, nextClassification: string, nextState: string) => {
        fetchPage(nextQuery, nextClassification, nextState, 0, false);
      },
      300,
      { leading: false, trailing: true },
    ),
  ).current;

  useEffect(() => {
    throttledSearch(query, classification, state);
  }, [query, classification, state, throttledSearch]);

  useEffect(() => {
    return () => {
      throttledSearch.cancel();
    };
  }, [throttledSearch]);

  const loadMore = useCallback(async () => {
    if (!query && !classification && !state) return;
    if (clinics.length >= total) return;
    await fetchPage(query, classification, state, offset, true);
  }, [query, classification, state, clinics.length, total, offset, fetchPage]);

  const onChipClick = (value: string) => {
    setClassification((prev) => (prev === value ? '' : value));
  };

  // International / Puerto Rico is no longer a fixed option here — it's now
  // selected via the State dropdown on the registration form and short-circuits
  // this modal entirely.
  const fixedOptions: Array<{ label: string; clinic: IClinic; icon?: any }> = [
    { label: 'Relief Vet', clinic: emptyClinic({ clinic_name: 'Relief Vet' }) },
    { label: 'Student', clinic: emptyClinic({ clinic_name: 'Student' }) },
    {
      label: 'Not Applicable',
      clinic: emptyClinic({ clinic_name: 'Not Applicable' }),
    },
    {
      label: 'Add My Own Clinic',
      clinic: emptyClinic({ clinic_name: 'Add My Own Clinic' }),
      icon: addCircle,
    },
  ];

  const hasFilter = Boolean(query || classification || state);
  const hasMore = clinics.length < total;

  return (
    <IonPage className="elanco-modal">
      <IonHeader className="elanco-modal-header">
        <IonToolbar>
          <IonButtons>
            <button className="elanco-modal-close" onClick={onDismiss}>
              <IonIcon color="secondary" slot="end" icon={closeCircle} />
            </button>
          </IonButtons>
          <IonSearchbar
            className="elanco-modal-search"
            onIonChange={(e) => setQuery(e.detail.value || '')}
            placeholder="Search Clinic Name or City"
            showClearButton="focus"
          />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {classifications.length > 0 && (
          <div className="elanco-modal-chips">
            {classifications.map((c) => (
              <IonChip
                key={c.value}
                outline={classification !== c.value}
                color="secondary"
                onClick={() => onChipClick(c.value)}>
                <IonLabel>{c.value}</IonLabel>
              </IonChip>
            ))}
          </div>
        )}
        <IonList className="elanco-modal-list" lines="none">
          {fixedOptions.map((opt) => (
            <IonItem
              key={opt.label}
              onClick={() => {
                onDismiss();
                onSelectItem(opt.clinic);
              }}>
              {opt.label}
              {opt.icon ? (
                <IonIcon color="secondary" className="ml-2" icon={opt.icon} />
              ) : null}
            </IonItem>
          ))}
          <IonItem>
            <hr className="elanco-divider" />
          </IonItem>

          {!hasFilter && (
            <IonItem lines="none">
              <IonText color="medium">
                <p className="text-small">
                  Start typing or pick a category above to search clinics.
                </p>
              </IonText>
            </IonItem>
          )}

          {hasFilter && !loading && clinics.length === 0 && (
            <IonItem lines="none">
              <IonText color="medium">
                <p className="text-small">No clinics match this search.</p>
              </IonText>
            </IonItem>
          )}

          {clinics
            .filter((clinic: IClinic) => clinic.crm_id !== 'INTL-PR')
            .map((clinic: IClinic) => (
              <IonItem
                key={clinic.crm_id}
                className="ion-text-capitalize"
                onClick={() => {
                  onDismiss();
                  onSelectItem(clinic);
                }}>
                <IonText>
                  {clinic.clinic_name.toLocaleLowerCase()}
                  <div className="elanco-modal--address">
                    {(clinic.address_1 || '').toLocaleLowerCase()},{' '}
                    {(clinic.city || '').toLocaleLowerCase()},{' '}
                    {(clinic.state_cd || '').toLocaleUpperCase()}{' '}
                    {(clinic.zip || '').toLocaleLowerCase()}
                  </div>
                </IonText>
              </IonItem>
            ))}

          {hasFilter && loading && (
            <IonItem lines="none">
              <IonSpinner name="dots" />
            </IonItem>
          )}
        </IonList>

        <IonInfiniteScroll
          threshold="200px"
          disabled={!hasFilter || !hasMore}
          onIonInfinite={async (e) => {
            await loadMore();
            (e.target as HTMLIonInfiniteScrollElement).complete();
          }}>
          <IonInfiniteScrollContent loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

const ClinicModal: React.FC<{
  onChange: (param: Object) => void;
  onBlur: () => void;
  value?: IClinic;
  state?: string;
  disabled?: boolean;
}> = ({ onChange, onBlur, value, state = '', disabled = false }) => {
  const { showConfirmAddress } = useConfirmAddress();
  const handleClick = (clinic: IClinic) => {
    // if clinic is custom dont display modal
    if (
      clinic.clinic_name === 'Add My Own Clinic' ||
      clinic.clinic_name === 'Student' ||
      clinic.clinic_name === 'Not Applicable' ||
      clinic.clinic_name === 'Relief Vet'
    ) {
      onChange({
        clinic_name: clinic.clinic_name,
        crm_id: clinic.crm_id,
      });
      return;
    }

    const addressClass =
      clinic.address_1.length > 23 ? 'item--clinic-address' : '';

    // create modal description
    const modalDescription = `
      <span class="${addressClass}">
        ${clinic.address_1.toLocaleLowerCase()} <br />
      </span>
      ${clinic.city.toLocaleLowerCase()},
      ${clinic.state_cd.toLocaleUpperCase()}
      ${clinic.zip}
    `;

    // crea figmte modal and wait for response
    const modal = showConfirmAddress(modalDescription);
    modal.then((isConfirmed) => {
      if (isConfirmed) {
        onChange({ crm_id: clinic.crm_id, clinic_name: clinic.clinic_name });
      } else {
        onChange({ clinic_name: '' });
      }
    });
  };

  const handleDismiss = () => {
    dismiss();
  };

  const [present, dismiss] = useIonModal(ClinicModalBody, {
    onDismiss: handleDismiss,
    onSelectItem: handleClick,
    state,
  });

  return (
    <div className="elanco--item">
      <label id="clinic">
        Clinic Name <IonText color="danger">*</IonText>
      </label>
      <input
        type="text"
        name="clinic"
        readOnly
        autoComplete="off"
        disabled={disabled}
        placeholder={disabled ? 'Please select a state first' : undefined}
        value={value?.clinic_name || ''}
        onBlur={onBlur}
        onClick={() => {
          if (disabled) return;
          present();
        }}
      />
    </div>
  );
};

export default ClinicModal;
